/**
 * State-machine engine (PROJECT_SPEC.md §14 step 4; 21 CFR 11.10(f) operational system checks; N7).
 * A machine is DATA: a transition table. The same table drives server-side enforcement (authoritative,
 * inside the append transaction), optimistic client checks, the WF-001/WF-002 exhaustive test matrix
 * and the design-spec export. Guards are named pure functions evaluated over facts the caller loaded.
 */

export type GuardVerdict = { ok: true } | { ok: false; code: string; message: string };
export type Guard = (facts: Readonly<Record<string, unknown>>) => GuardVerdict;

export interface Transition<S extends string, E extends string> {
  from: S;
  on: E;
  to: S;
  /** All named guards must pass. Names resolve through the registry passed to transition(). */
  guards?: readonly string[];
  /** If set, the actor must hold at least one of these permissions (server-side authority check, 11.10(g)). */
  permissions?: readonly string[];
  /** Regulatory hook for the traceability matrix. */
  cite?: string;
}

export interface Machine<S extends string, E extends string> {
  id: string;
  initial: S;
  states: readonly S[];
  events: readonly E[];
  transitions: readonly Transition<S, E>[];
  /** Terminal states: no outgoing transitions are allowed even if a row were added by mistake. */
  terminal?: readonly S[];
}

export type TransitionResult<S extends string> =
  | { ok: true; to: S; transition: Transition<S, string> }
  | {
      ok: false;
      code: "FORBIDDEN_TRANSITION" | "GUARD_FAILED" | "FORBIDDEN_ACTOR" | "UNKNOWN_STATE" | "UNKNOWN_EVENT";
      message: string;
      guard?: string;
      /**
       * The failing guard's own verdict code, e.g. SCALE_CHECK_DUE. `code` says a guard refused; this says
       * which rule and in the vocabulary the operator's screen speaks, so the refusal is actionable rather
       * than merely negative.
       */
      guardCode?: string;
    };

export interface TransitionContext {
  permissions: ReadonlySet<string>;
  facts: Readonly<Record<string, unknown>>;
  guards: Readonly<Record<string, Guard>>;
}

export function findTransition<S extends string, E extends string>(
  m: Machine<S, E>,
  from: S,
  on: E,
): Transition<S, E> | undefined {
  return m.transitions.find((t) => t.from === from && t.on === on);
}

/** Pure decision: the caller (server or client) supplies facts; the server's decision is the only one that counts (§8.3). */
export function transition<S extends string, E extends string>(
  m: Machine<S, E>,
  from: S,
  on: E,
  ctx: TransitionContext,
): TransitionResult<S> {
  if (!m.states.includes(from))
    return { ok: false, code: "UNKNOWN_STATE", message: `${m.id}: unknown state ${from}` };
  if (!m.events.includes(on))
    return { ok: false, code: "UNKNOWN_EVENT", message: `${m.id}: unknown event ${on}` };
  const t = findTransition(m, from, on);
  if (!t || m.terminal?.includes(from))
    return {
      ok: false,
      code: "FORBIDDEN_TRANSITION",
      message: `${m.id}: ${on} is not permitted in state ${from}`,
    };
  if (t.permissions && !t.permissions.some((p) => ctx.permissions.has(p)))
    return {
      ok: false,
      code: "FORBIDDEN_ACTOR",
      message: `${m.id}: ${on} requires one of [${t.permissions.join(", ")}]`,
    };
  for (const name of t.guards ?? []) {
    const g = ctx.guards[name];
    if (!g)
      return {
        ok: false,
        code: "GUARD_FAILED",
        guard: name,
        message: `${m.id}: guard ${name} is not registered`,
      };
    const v = g(ctx.facts);
    if (!v.ok)
      return {
        ok: false,
        code: "GUARD_FAILED",
        guard: name,
        guardCode: v.code,
        message: `${m.id}: ${v.message}`,
      };
  }
  return { ok: true, to: t.to, transition: t as Transition<S, string> };
}

/** Every (state, event) pair — the WF-001/WF-002 matrix is generated from this, so it can never drift from the table. */
export function matrix<S extends string, E extends string>(
  m: Machine<S, E>,
): { from: S; on: E; permitted: boolean; to?: S }[] {
  const out: { from: S; on: E; permitted: boolean; to?: S }[] = [];
  for (const from of m.states)
    for (const on of m.events) {
      const t = findTransition(m, from, on);
      const permitted = !!t && !m.terminal?.includes(from);
      out.push(permitted && t ? { from, on, permitted, to: t.to } : { from, on, permitted: false });
    }
  return out;
}

/** Static sanity for every machine: rows reference declared states/events, no duplicate (from,on), initial declared. */
export function validateMachine<S extends string, E extends string>(m: Machine<S, E>): string[] {
  const problems: string[] = [];
  if (!m.states.includes(m.initial)) problems.push(`${m.id}: initial state ${m.initial} not declared`);
  const seen = new Set<string>();
  for (const t of m.transitions) {
    if (!m.states.includes(t.from)) problems.push(`${m.id}: transition from undeclared state ${t.from}`);
    if (!m.states.includes(t.to)) problems.push(`${m.id}: transition to undeclared state ${t.to}`);
    if (!m.events.includes(t.on)) problems.push(`${m.id}: transition on undeclared event ${t.on}`);
    const k = `${t.from}|${t.on}`;
    if (seen.has(k)) problems.push(`${m.id}: duplicate transition ${k}`);
    seen.add(k);
    if (m.terminal?.includes(t.from)) problems.push(`${m.id}: transition out of terminal state ${t.from}`);
  }
  return problems;
}
